import { Group, Text } from "@mantine/core";
import React from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { isEntityMcaLocked } from "./mca/mcaApprovalLabels";
import OrderConfirmPolicyButton from "./orderConfirmPolicyButton";
import OrderEditPolicyButton from "./orderEditPolicyButton";
import OrderPrintSubmissionCoverSheetActionButton from "./orderPrintSubmissionCoverSheetActionButton";
import OrderPrintReceiveCoverSheetActionButton from "./orderPrintReceiveCoverSheetActionButton";
import OrderTerminateActionButton from "./orderTerminateActionButton";
import OrderEditButton from "./orderEditButton";
import OrderAdjustButton from "./orderAdjustButton";
import OrderReinstantiateButton from "./orderReinstantiateButton";
import NewTicketButton from "./newTicketButton";
import OrderDeliveryPolicyButton from "./orderDeliverPolicyButton";
import OrderCancelButton from "./orderCancelButton";
import OrderRedoCancelButton from "./orderRedoCancelButton";
import OrderAddRiderActionButton from "./orderAddRiderActionButton";
import ActionPermissionGate from "./actionPermissionGate";
import { useActionRight } from "../hooks/useActionRight";
import { useHideFormActionList } from "../hooks/useHideFormActionList";

const ORDER_STATUS_ALLOWED_TO_EDIT = [
  "DFT",
  "CAN",
  "RCV",
  "SUB",
  "PND",
  "AIP",
  "UNKNOWN",
];

const ORDER_STATUS_ALLOW_CONSULTANT_TO_EDIT = ["DFT", "CAN"];
const ORDER_STATUS_ALLOWED_TO_EDIT_POLICY = [
  "INF",
  "RCV",
  "SUB",
  "PND",
  "AIP",
  "ACT",
];

const ORDER_STATUS_ALLOWED_TO_RECEIVE = ["DFT", "RCV"];
const ORDER_STATUS_ALLOWED_TO_ADJUST = ["INF"];
const ORDER_STATUS_ALLOWED_TO_CONFIRM = [
  "RCV",
  "SUB",
  "PND",
  "AIP",
  "ACT",
  "UNKNOWN",
];

const ORDER_STATUS_ALLOWED_TO_REINSTANT = [
  "TER",
  "WTH",
  "COL",
  "SUR",
  "REJ",
  "LAP",
];

const OrderActionList = ({ form, role }) => {
  const hideActionList = useHideFormActionList(form);
  const { status, code, consultants, _id, policyNumber } = form.values;
  const isMcaLocked = isEntityMcaLocked(form.values);

  const allowToEdit = () =>
    !isMcaLocked && ORDER_STATUS_ALLOWED_TO_EDIT.includes(status);
  const allowToEditByConsultant = () =>
    !isMcaLocked && ORDER_STATUS_ALLOW_CONSULTANT_TO_EDIT.includes(status);
  const allowToEditPolicy = () =>
    !isMcaLocked &&
    ORDER_STATUS_ALLOWED_TO_EDIT_POLICY.includes(status) &&
    !_.isEmpty(policyNumber);
  const allowToAdjust = () =>
    !isMcaLocked && ORDER_STATUS_ALLOWED_TO_ADJUST.includes(status);
  const allowToConfirm = () =>
    !isMcaLocked && ORDER_STATUS_ALLOWED_TO_CONFIRM.includes(status);
  const allowToReceive = () =>
    !isMcaLocked && ORDER_STATUS_ALLOWED_TO_RECEIVE.includes(status);
  const allowToReinstantiate = () =>
    !isMcaLocked && ORDER_STATUS_ALLOWED_TO_REINSTANT.includes(status);
  const allowToDeliver = () => !isMcaLocked && status === "INF";
  const allowToCancel = () => !isMcaLocked && status === "DFT";
  const allowToRedoCancel = () => !isMcaLocked && status === "CAN";

  const receiveCoverSheetLabel =
    role === "CONSULTANT" || role === "MANAGEMENT"
      ? "Print Cover Sheet"
      : "Receive Order";
  const receiveAction =
    role === "CONSULTANT" || role === "MANAGEMENT" ? "print" : "receive";

  const { t } = useTranslation();
  const { isActionVisible } = useActionRight();

  if (hideActionList) return null;

  const showStaffEditOrder =
    allowToEdit() && isActionVisible("order", "editOrder");
  const showConsultantEditOrder =
    allowToEditByConsultant() &&
    isActionVisible("order", "editOrderConsultant") &&
    !showStaffEditOrder;

  return (
    <Group mt={10}>
      {isMcaLocked && (
        <Text size="xs" color="dimmed">
          Workflow actions are disabled while this order is pending MCA approval.
        </Text>
      )}

      {!isMcaLocked && (
        <ActionPermissionGate entity="order" action="addRider">
          <OrderAddRiderActionButton mainForm={form} label={t("Add New Rider")} />
        </ActionPermissionGate>
      )}

      {showStaffEditOrder && (
        <ActionPermissionGate entity="order" action="editOrder">
          <OrderEditButton mainForm={form} label={t("Edit Order Details")} />
        </ActionPermissionGate>
      )}

      {showConsultantEditOrder && (
        <ActionPermissionGate entity="order" action="editOrderConsultant">
          <OrderEditButton
            mainForm={form}
            label={t("Edit Order Details (Consultant)")}
          />
        </ActionPermissionGate>
      )}

      {allowToAdjust() && (
        <ActionPermissionGate entity="order" action="adjustOrder">
          <OrderAdjustButton mainForm={form} label={t("Adjust Order")} />
        </ActionPermissionGate>
      )}

      {allowToReceive() && (
        <ActionPermissionGate
          entity="order"
          action="printReceiveCoverSheet"
          requireFullAccess={false}
        >
          <OrderPrintReceiveCoverSheetActionButton
            mainForm={form}
            label={receiveCoverSheetLabel}
            action={receiveAction}
          />
        </ActionPermissionGate>
      )}

      <ActionPermissionGate
        entity="order"
        action="printSubmitCoverSheet"
        requireFullAccess={false}
      >
        <OrderPrintSubmissionCoverSheetActionButton
          mainForm={form}
          label={t("Print Submit Cover Sheet")}
        />
      </ActionPermissionGate>

      {allowToEditPolicy() && (
        <ActionPermissionGate entity="order" action="editPolicy">
          <OrderEditPolicyButton mainForm={form} label={t("Edit Policy")} />
        </ActionPermissionGate>
      )}

      {allowToConfirm() && (
        <ActionPermissionGate entity="order" action="confirmPolicy">
          <OrderConfirmPolicyButton
            mainForm={form}
            label={t("Confirm Policy")}
          />
        </ActionPermissionGate>
      )}

      {allowToDeliver() && (
        <ActionPermissionGate entity="order" action="deliverPolicy">
          <OrderDeliveryPolicyButton
            mainForm={form}
            label={t("Deliver Policy")}
          />
        </ActionPermissionGate>
      )}

      <ActionPermissionGate entity="order" action="createTicket">
        <NewTicketButton
          buttonType="fullWidth"
          code={code}
          entityId={_id}
          entity="order"
          ticketType={"TK-ORDER"}
          members={consultants?.map((c) => c._id)}
          label={t("Create Ticket")}
        />
      </ActionPermissionGate>

      {!isMcaLocked && (
        <ActionPermissionGate entity="order" action="terminatePolicy">
          <OrderTerminateActionButton
            mainForm={form}
            label={t("Terminate Policy")}
          />
        </ActionPermissionGate>
      )}

      {allowToCancel() && (
        <ActionPermissionGate entity="order" action="cancelOrder">
          <OrderCancelButton mainForm={form} label={t("Cancel Order")} />
        </ActionPermissionGate>
      )}

      {allowToRedoCancel() && (
        <ActionPermissionGate entity="order" action="redoCancelOrder">
          <OrderRedoCancelButton
            mainForm={form}
            label={t("Redo Cancel Order")}
          />
        </ActionPermissionGate>
      )}

      {allowToReinstantiate() && (
        <ActionPermissionGate entity="order" action="reinstantiateOrder">
          <OrderReinstantiateButton
            mainForm={form}
            label={t("Reinstantiate Order")}
          />
        </ActionPermissionGate>
      )}
    </Group>
  );
};

export default OrderActionList;
